import { useState, type ReactNode } from "react";
import { cx, MarkerIcon } from "../shared/markers";
import Disclaimer from "./Disclaimer";
import { mouseHover } from "./hover";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import type { PartnerMapAction } from "./track";
import type { Lang, PartnerMapContent, Tour, WeekProgram } from "./types";
import { placeFill, walkLabel, type MappedPlace } from "./walk";
import {
	bundleTours,
	dateOf,
	dayOrder,
	durationSummary,
	eventsOn,
	firstTime,
	laterTimes,
	niceDate,
	shortDate,
	toursOn,
	tripsOn,
	upcoming,
} from "./weekProgram";
import styles from "./PartnerMap.module.scss";

interface DayStripProps {
	lang?: Lang;
	today: Date;
	todayIdx: number;
	day: number;
	onDay: (day: number) => void;
}

/** The seven days from today on, the chosen one filled; it sits above the list, which scrolls under it. */
export const DayStrip = ({ lang, today, todayIdx, day, onDay }: DayStripProps) => {
	const t = strings(lang);
	return (
		<div className={styles.days} role="group" aria-label={t.dayOfWeek}>
			{dayOrder(todayIdx).map((i) => (
				<button
					key={i}
					type="button"
					id={`partnermap-day-${i}`}
					className={cx(styles.day, i === todayIdx && styles.today)}
					aria-pressed={i === day}
					onClick={() => onDay(i)}
				>
					{t.days[i].slice(0, 3)}
					<small>{shortDate(today, i)}</small>
				</button>
			))}
		</div>
	);
};

type OnTrack = (action: PartnerMapAction, item?: string) => void;

/** A card of the strips under the day: a link when it has somewhere to go, a plain card when it has not. */
const Card = ({ id, url, onClick, children }: { id: string; url?: string; onClick?: () => void; children: ReactNode }) =>
	url ? (
		<a id={id} className={styles.trip} href={url} target="_blank" rel="noopener" onClick={onClick}>
			{children}
		</a>
	) : (
		<div id={id} className={styles.trip}>
			{children}
		</div>
	);

/** The icon square of a card: the program's photo of the event or tour when it has one, else its icon. */
const CardIcon = ({ image, className, children }: { image?: string; className?: string; children: ReactNode }) => (
	<span className={cx(styles.tripIc, className)}>{image ? <img src={image} alt="" width={44} height={44} loading="lazy" /> : children}</span>
);

/** A tour as a card of the "Tours & trips" list: its photo or an icon, what it is, its kind and length, and its link. */
const TourCard = ({ tour, lang, onTrack }: { tour: Tour; lang?: Lang; onTrack: OnTrack }) => (
	<Card id={`partnermap-tour-${tour.id}`} url={tour.url} onClick={() => onTrack("tickets", tour.id)}>
		<CardIcon image={tour.image}>
			<UiIcon name="directions" />
		</CardIcon>
		<span>
			<b>{tour.title}</b>
			{tour.note && <span>{tour.note}</span>}
			{(tour.kind || tour.duration) && <em>{[tour.kind, tour.duration].filter(Boolean).join(" · ")}</em>}
		</span>
		{tour.url && <u>{strings(lang).book}</u>}
	</Card>
);

/** The variants of one trip behind one row: the row is the button that opens their list, each variant a card as it is on its own. */
const TourBundle = ({ group, tours, lang, onTrack }: { group: string; tours: Tour[]; lang?: Lang; onTrack: OnTrack }) => {
	const t = strings(lang);
	const [open, setOpen] = useState(false);
	const listId = `partnermap-bundle-${tours[0].id}`;
	const duration = durationSummary(tours);
	return (
		<div>
			<button type="button" className={cx(styles.trip, styles.bundle)} aria-expanded={open} aria-controls={listId} onClick={() => setOpen(!open)}>
				<CardIcon image={tours.find((t) => t.image)?.image}>
					<UiIcon name="directions" />
				</CardIcon>
				<span>
					<b>{group}</b>
					<em>{`${t.options(tours.length)}${duration ? ` · ${duration}` : ""}`}</em>
				</span>
				<i className={styles.chev} aria-hidden="true" />
			</button>
			<div id={listId} hidden={!open}>
				<div className={cx(styles.trips, styles.variants)}>
					{tours.map((tour) => (
						<TourCard key={tour.id} tour={tour} lang={lang} onTrack={onTrack} />
					))}
				</div>
			</div>
		</div>
	);
};

interface WeekTabProps {
	content: PartnerMapContent;
	program: WeekProgram;
	places: Record<string, MappedPlace>;
	today: Date;
	todayIdx: number;
	day: number;
	/** the id of the event that is open */
	activeEvent: string | null;
	/** "base" or a place's slug: the event whose place a mouse is over, on the list or on the map */
	hover: string | null;
	onOpenEvent: (id: string) => void;
	onHover: (key: string | null) => void;
	onTrack: OnTrack;
}

/**
 * The Week program tab: the day's events as a timeline, the day trips that run that day with the partner's code, the one-night
 * events still to come, and its tours, the variants of one trip collapsed behind a row.
 */
const WeekTab = ({ content, program, places, today, todayIdx, day, activeEvent, hover, onOpenEvent, onHover, onTrack }: WeekTabProps) => {
	const events = eventsOn(program, day, today);
	const trips = tripsOn(program, day);
	const tours = toursOn(program, day);
	const coming = upcoming(program, today);
	const { dayTrips } = program;
	const { lang } = content;
	const t = strings(lang);

	return (
		<>
			<div className={styles.dayhead}>
				<h2>{day === todayIdx ? t.today : t.days[day]}</h2>
				<span>
					{dateOf(today, day, lang).replace(/^\w+ /, "")} · {t.thingsOn(events.length)}
				</span>
			</div>
			{events.map((e) => {
				const place = e.where === "hostel" ? null : places[e.where];
				const hoverKey = place ? place.slug : "base";
				const later = laterTimes(e, lang);
				const price = e.price ? <span className={cx(styles.price, e.price === t.free && styles.free)}>{e.price}</span> : null;
				return (
					<button
						key={e.id}
						type="button"
						id={`partnermap-event-${e.id}`}
						className={cx(styles.ev, activeEvent === e.id && styles.sel, hover === hoverKey && styles.on)}
						onClick={() => onOpenEvent(e.id)}
						{...mouseHover(onHover, hoverKey)}
					>
						<span className={styles.t}>
							{firstTime(e)}
							{e.date && <small>{t.oneNightOnly}</small>}
						</span>
						<span>
							<b>{e.title}</b>
							<span className={styles.n}>{e.note}</span>
							{later && <span className={styles.also}>{t.alsoAt(later)}</span>}
							<span className={styles.w}>
								{place ? (
									<i style={{ background: placeFill(content.theme, place.kind) }}>
										<MarkerIcon name={place.icon} size={9} />
									</i>
								) : (
									<img src={content.partner.logo} alt="" />
								)}
								{place ? `${place.name} · ${walkLabel(place.km, content.rideLabel, lang, content.bikeFromKm)}` : t.atHostel}
							</span>
						</span>
						{e.image ? (
							<span className={styles.end}>
								{price}
								<img className={styles.pic} src={e.image} alt="" width={46} height={46} loading="lazy" />
							</span>
						) : (
							(price ?? <span />)
						)}
					</button>
				);
			})}
			{dayTrips && trips.length > 0 && (
				<>
					<div className={styles.sec}>
						{t.dayTripsOn(t.days[day])} <i>{trips.length}</i>
					</div>
					<div className={styles.trips}>
						{trips.map((trip) => (
							<a
								key={trip.id}
								id={`partnermap-trip-${trip.id}`}
								className={styles.trip}
								href={trip.url}
								target="_blank"
								rel="noopener"
								onClick={() => onTrack("tickets", trip.id)}
							>
								<span className={styles.tripIc}>
									<UiIcon name={trip.icon} />
								</span>
								<span>
									<b>{trip.title}</b>
									<span>{trip.note}</span>
									<em>{t.discountOff(dayTrips.discount)}</em>
								</span>
								<u>{t.book}</u>
							</a>
						))}
					</div>
					<div className={styles.code}>
						{t.useCodeBefore}
						<b>{dayTrips.code}</b>
						{t.useCodeAfter(dayTrips.discount)}
					</div>
				</>
			)}
			{coming.length > 0 && (
				<>
					<div className={styles.sec}>
						{t.comingUp} <i>{coming.length}</i>
					</div>
					<div className={styles.trips}>
						{coming.map((e) => (
							<Card key={e.id} id={`partnermap-coming-${e.id}`} url={e.booking?.url} onClick={() => onTrack("tickets", e.id)}>
								<CardIcon image={e.image} className={styles.tripMk}>
									<MarkerIcon name={e.icon} size={22} />
								</CardIcon>
								<span>
									<b>{e.title}</b>
									{e.note && <span>{e.note}</span>}
									<em>{`${niceDate(e.date!, lang)}${firstTime(e) ? ` · ${t.fromTime(firstTime(e))}` : ""}`}</em>
								</span>
								{e.booking && <u>{e.booking.cta}</u>}
							</Card>
						))}
					</div>
				</>
			)}
			{tours.length > 0 && (
				<>
					<div className={styles.sec}>
						{t.toursTrips} <i>{tours.length}</i>
					</div>
					<div className={styles.trips}>
						{bundleTours(tours).map((row) =>
							"tour" in row ? (
								<TourCard key={row.tour.id} tour={row.tour} lang={lang} onTrack={onTrack} />
							) : (
								<TourBundle key={row.group} {...row} lang={lang} onTrack={onTrack} />
							)
						)}
					</div>
					{content.info?.events && (
						<a className={styles.all} href={content.info.events} target="_blank" rel="noopener" onClick={() => onTrack("contact", "events")}>
							{t.showAllTours(content.partner.shortName)} <span aria-hidden="true">→</span>
						</a>
					)}
				</>
			)}
			<p className={styles.fine}>{program.fineprint}</p>
			<Disclaimer content={content} />
		</>
	);
};

export default WeekTab;
